import { getElse, getAssert } from "../utils/Safe";

/**
 * Defines the structure of an audio channel in a config.
 * @internal
 */
export class ChannelModel
{
    constructor()
    {
        /**
         * Channel volume. Between 0 and 1.
         * Used to scale track volume.
         * @type {number}
         */
        this.volume = 0;
        /**
         * Specifies if a channel can only play one track at a time.
         * @type {boolean}
         */
        this.singleTrack = false;
        /**
         * A map of track models belonging to this channel.
         * @type {Object.<string, TrackModel>}
         */
        this.trackMap = null;
    }

    /**
     * Cleans up the ChannelModel.
     */
    destroy()
    {
        let keys = Object.keys(this.trackMap)
        while (keys.length > 0)
        {
            let key = keys.pop();
            delete this.trackMap[key];
        }
    }
}

/**
 * Defines an audio track in a config.
 * @internal
 */
export class TrackModel
{
    constructor()
    {
        /**
         * Track volume. Between 0 and 1. 
         * This is multiplied by the channel volume.
         * @type {number}
         */
        this.volume = 0;
        /**
         * Specifies if the track loops.
         * @type {boolean}
         */
        this.looping = false;
        /**
         * Track play priority.
         * Tracks will higher priority will interrupt lower priority
         * tracks in a single track channel.
         * @type {number}
         */
        this.priority = 0;
    }
}

/**
 * Parses the config and returns a safe ChannelModel object.
 * @param {*} config 
 * @internal
 */
export function getAudioChannelModel(config)
{
    let model = new ChannelModel();

    model.volume = getElse([config, "volume"], 1);
    model.singleTrack = getElse([config, "singleTrack"], false);
    model.trackMap = { };

    let map = getElse([config, "trackMap"], { });
    for (let key in map)
    {
        model.trackMap[key] = getAudioTrackModel(map[key]);
    }

    return model;
}

/** 
 * Parses the config and returns a safe TrackModel object.
 * @param {*} config 
 * @internal
 */
export function getAudioTrackModel(config)
{
    let model = new TrackModel();

    model.volume = getElse([config, "volume"], 1);
    model.looping = getElse([config, "looping"], false);
    model.priority = getElse([config, "priority"], 0);

    return model;
}